import { fabric } from 'fabric';
import { useCallback } from 'react';
import Vec2 from 'vec2';

import { CANVAS_OBJECT_TYPE_PREFIX } from '~/core/constants';
import { UserDesignOperation } from '~/core/enums';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import { updateShapeByTranslation } from '~/core/helpers';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { DrawingShape } from '~/core/models';
import {
  useDrawingShapeContext,
  useFabricCanvasContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  setPreventRepricing,
  updateNeedRedrawCanvas,
} from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { updateSelectedDrawingTab } from '~/redux/slices/shape';

/**
 * Synchronizes the position of the fabric.Object (the appliance in canvas) with the drawing shapes
 * @param fabricObj - the fabric.Object equivalent to the appliance being moved
 * @param drawingShapes
 */
const updateAppliancePositionInShape = (
  fabricObj: fabric.Object,
  drawingShapes: DrawingShape[]
): void => {
  const namePrefix = `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_`;
  const applianceId = fabricObj.name?.replace(namePrefix, '');

  if (!applianceId) {
    return;
  }

  const appliance = drawingShapes
    .flatMap((shape) => shape.appliances ?? [])
    .find(({ id }) => id === applianceId);

  if (appliance) {
    appliance.centerPoint = new Vec2(fabricObj.left ?? 0, fabricObj.top ?? 0);
  }
};

export const useMouseUp = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);

  const {
    getters: { numberOfShapesInTheDrawingArea },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const { drawingShapesRef, movingItemRef } = useFabricCanvasContext();

  const { registerUserAction } = useRegisterUserAction();

  const handleShapeMovement = useCallback(
    (moveVec: Vec2) => {
      if (numberOfShapesInTheDrawingArea > 1) {
        registerUserAction(UserDesignOperation.MOVE_SHAPE);
      }
      dispatch(setPreventRepricing(true));
      const shape = movingItemRef.current as DrawingShape;

      updateShapeByTranslation(moveVec, shape, canvas);
      updateDrawingShapes([...drawingShapesRef.current]);
    },
    [
      canvas,
      dispatch,
      drawingShapesRef,
      movingItemRef,
      numberOfShapesInTheDrawingArea,
      registerUserAction,
      updateDrawingShapes,
    ]
  );

  // Sync modified shapes to context - call AFTER clearing activeDrawingApplianceId
  const syncShapesToContext = useCallback(
    (fabricObj?: fabric.Object) => {
      if (fabricObj) {
        updateAppliancePositionInShape(fabricObj, drawingShapesRef.current);
      }
      updateDrawingShapes([...drawingShapesRef.current]);
    },
    [drawingShapesRef, updateDrawingShapes]
  );

  const finalizeDraggingState = useCallback(() => {
    dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));

    // FIX: Force the redraw effect to trigger by ensuring state actually changes.
    // If needRedrawCanvas is already true, setting it to true again won't trigger
    // the effect. By first setting to false, we guarantee a state change.
    dispatch(updateNeedRedrawCanvas(false));
    queueMicrotask(() => {
      dispatch(updateNeedRedrawCanvas(true));
    });

    movingItemRef.current = undefined;
  }, [dispatch, movingItemRef]);

  return {
    finalizeDraggingState,
    handleShapeMovement,
    syncShapesToContext,
  };
};
